import { useEffect, useState, useRef, createContext, useContext, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Scroll position storage - persists across navigation
const scrollPositions = new Map();

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import BrowseDebates from "@/pages/BrowseDebates";
import DebateRoom from "@/pages/DebateRoom";
import CreateDebate from "@/pages/CreateDebate";
import ProfilePage from "@/pages/ProfilePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import MyDebates from "@/pages/MyDebates";
import AdminPage from "@/pages/AdminPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import GroupsPage from "@/pages/GroupsPage";
import PracticeMode from "@/pages/PracticeMode";
import ArenaPage from "@/pages/ArenaPage";

// Components
import MusicPlayer from "@/components/MusicPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Robust token storage that works on iOS Safari
// iOS Safari can be aggressive about clearing localStorage in certain contexts
const tokenStorage = {
  get: () => {
    try {
      // Try localStorage first
      const token = localStorage.getItem("token");
      if (token) return token;
      // Fallback to sessionStorage (survives page reloads but not tab closes)
      return sessionStorage.getItem("token_backup");
    } catch (e) {
      console.error("[TokenStorage] Failed to get token:", e);
      return null;
    }
  },
  set: (token) => {
    try {
      localStorage.setItem("token", token);
      // Also save backup in sessionStorage for iOS Safari
      sessionStorage.setItem("token_backup", token);
    } catch (e) {
      console.error("[TokenStorage] Failed to set token:", e);
      // If localStorage fails, at least try sessionStorage
      try {
        sessionStorage.setItem("token_backup", token);
      } catch (e2) {
        console.error("[TokenStorage] SessionStorage also failed:", e2);
      }
    }
  },
  remove: () => {
    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token_backup");
    } catch (e) {
      console.error("[TokenStorage] Failed to remove token:", e);
    }
  }
};

// Auth Context
const AuthContext = createContext(null);

// Recording Context - to communicate recording state to MusicPlayer
const RecordingContext = createContext({ isRecording: false, setIsRecording: () => {} });
export const useRecording = () => useContext(RecordingContext);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(tokenStorage.get());

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = tokenStorage.get();
      console.log("[Auth] Checking saved token:", savedToken ? "exists" : "none");
      
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { 
              'Authorization': `Bearer ${savedToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 15000 // 15 second timeout for PWA/slow networks
          });
          console.log("[Auth] Token valid, user:", response.data?.username);
          setUser(response.data);
          setToken(savedToken);
          // Ensure token is synced in both storages
          tokenStorage.set(savedToken);
        } catch (e) {
          console.error("[Auth] Token validation failed:", e.response?.status, e.message);
          
          // Only clear token if it's actually invalid (401/403), not network errors
          if (e.response?.status === 401 || e.response?.status === 403) {
            console.log("[Auth] Clearing invalid token");
            tokenStorage.remove();
            setToken(null);
          } else {
            // Network error - keep the token and user, they might just be offline
            console.log("[Auth] Network error, keeping token");
            setToken(savedToken);
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    tokenStorage.set(authToken);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        timeout: 5000,
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (e) {
      // Ignore logout errors - we're logging out anyway
    }
    setUser(null);
    setToken(null);
    tokenStorage.remove();
  };

  // Helper to check permissions based on subscription
  const canVote = user?.subscription_tier === "access" || user?.subscription_tier === "pro" || user?.role === "admin";
  const canDebate = user?.subscription_tier === "pro" || user?.role === "admin" || user?.role === "debater";
  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser, canVote, canDebate, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Scroll Restoration Hook - saves position when leaving, restores when returning via back button
const ScrollRestoration = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  
  // Save scroll position when navigating away
  useEffect(() => {
    // When location changes, save the scroll position of the PREVIOUS page
    if (prevPathRef.current !== location.pathname) {
      scrollPositions.set(prevPathRef.current, window.scrollY);
      console.log(`[Scroll] Saved position ${window.scrollY} for ${prevPathRef.current}`);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);
  
  // Restore scroll position
  useLayoutEffect(() => {
    // Check if we have a saved position for this route
    const savedPosition = scrollPositions.get(location.pathname);
    
    if (savedPosition !== undefined) {
      // Delay to ensure DOM content is rendered
      setTimeout(() => {
        window.scrollTo({ top: savedPosition, behavior: 'instant' });
        console.log(`[Scroll] Restored position ${savedPosition} for ${location.pathname}`);
      }, 50);
    } else {
      // Scroll to top for new pages
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.key]);
  
  return null;
};

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        // Check for stored invite code (for new Google users)
        const storedInviteCode = sessionStorage.getItem("pendingInviteCode");
        
        try {
          const response = await axios.post(`${API}/auth/google/session`, 
            { session_id: sessionId, invite_code: storedInviteCode },
            { 
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              timeout: 15000 
            }
          );
          
          sessionStorage.removeItem("pendingInviteCode");
          
          if (response.data.user) {
            login(response.data.user, response.data.token || "google-session");
            toast.success("Welcome to Spardom!");
            navigate("/browse", { replace: true });
          }
        } catch (e) {
          const errorMsg = e.response?.data?.detail || "Authentication failed";
          if (errorMsg.includes("Invite code required")) {
            toast.error("Please register with an invite code first");
            navigate("/register", { replace: true });
          } else {
            toast.error(errorMsg);
            navigate("/login", { replace: true });
          }
        }
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for Google OAuth callback - detect session_id during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollRestoration />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/browse" element={<BrowseDebates />} />
        <Route path="/debate/:debateId" element={<DebateRoom />} />
        <Route path="/create" element={<CreateDebate />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/my-debates" element={<MyDebates />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/subscription/success" element={<SubscriptionPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/:groupId" element={<GroupsPage />} />
        <Route path="/practice" element={<PracticeMode />} />
        <Route path="/arena" element={<ArenaPage />} />
      </Routes>
    </>
  );
}

function App() {
  const [isRecording, setIsRecording] = useState(false);
  
  return (
    <div className="App min-h-screen bg-background">
      <div className="noise-overlay"></div>
      <BrowserRouter>
        <AuthProvider>
          <RecordingContext.Provider value={{ isRecording, setIsRecording }}>
            <AppRouter />
            <MusicPlayer isRecording={isRecording} />
            <Toaster position="top-right" richColors />
          </RecordingContext.Provider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
